import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { fetchAndActivate, getRemoteConfig } from 'firebase/remote-config';
import { firebaseApp } from 'firebaseConfig';
import { toast, ToastContainer } from 'react-toastify';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import useLocale, { UseLocale } from 'hooks/useLocale';
import useSearchHook, { UseSearch } from 'hooks/useSearch';
import { useAuth0User } from 'hooks/useAuth0User';
import { useSetTerminologySetting } from 'api/hooks/useSetTerminologySetting';
import { LocaleObject } from 'locale/locale';
import { withConfigurableTerms } from 'locale/withConfigurableTerms';

/* Interface for the app context */
interface AppState {
  search: UseSearch;
  locale: UseLocale;
}

/* Creating the context */
export const AppContext = createContext<AppState | undefined>(undefined);

const AppContextProvider: FC<{ children: ReactNode; loadRemoteCfg?: boolean }> = ({
  children,
  loadRemoteCfg = true,
}) => {
  const [loadingRemoteConfig, setLoadingRemoteConfig] = useState(true);
  const user = useAuth0User();
  const rawLocale = useLocale(user.isAuthenticated ? user.sub : '');
  const search = useSearchHook();

  // tenant-configured wording (e.g. Set vs Caddy) applied transparently to any lang string
  // containing its {{token}} - see locale/withConfigurableTerms and en.ts's ConfigurableTerms
  const { setTerminologySetting } = useSetTerminologySetting();
  const termValues = useMemo(() => ({ Set: setTerminologySetting }), [setTerminologySetting]);
  const locale: UseLocale = useMemo(
    () => [withConfigurableTerms(rawLocale[0], termValues), rawLocale[1], rawLocale[2]],
    [rawLocale, termValues],
  );

  useEffect(() => {
    if (loadRemoteCfg) {
      fetchAndActivate(getRemoteConfig(firebaseApp))
        .catch(err => {
          console.debug('Error fetching remote config', err);
        })
        .finally(() => {
          setLoadingRemoteConfig(false);
        });
    } else {
      setLoadingRemoteConfig(false);
    }
  }, [loadRemoteCfg]);

  const value = useMemo(
    () => ({
      search,
      locale,
    }),
    [search, locale],
  );

  return (
    <AppContext.Provider value={value}>
      <ToastContainer position={toast.POSITION.BOTTOM_CENTER} />
      {loadingRemoteConfig ? <PageSpinner /> : children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;

/* Searches for a set/device in the DB */
export const useSearch = (): AppState['search'] => {
  const context = useContext(AppContext);

  if (!context?.search) throw new Error('Search state must be consumed within AppProvider!');

  return context.search;
};

// resolves a dot-path (e.g. 'Settings.workflowPage') against the locale object's type
type DeepGet<T, P extends string> = P extends `${infer Head}.${infer Rest}`
  ? Head extends keyof T
    ? DeepGet<T[Head], Rest>
    : never
  : P extends keyof T
  ? T[P]
  : never;

export function useLanguage(): AppState['locale'];
export function useLanguage<P extends string>(scope: P): DeepGet<LocaleObject, P>;
export function useLanguage(scope?: string): AppState['locale'] | unknown {
  const context = useContext(AppContext);

  if (!context?.locale) throw new Error('Locale state must be consumed within AppProvider!');

  const [language] = context.locale;

  // stable across renders unless `scope` or the resolved language reference itself changes
  const scoped = useMemo(() => {
    if (scope === undefined) return undefined;
    return scope
      .split('.')
      .reduce<unknown>((node, key) => (node as Record<string, unknown> | undefined)?.[key], language);
  }, [language, scope]);

  return scope === undefined ? context.locale : scoped;
}
